import React, { useState, useEffect, useMemo } from "react";
import type { LearningModule, FlattenedSection, Step, Resource, ResourceType } from "./types";
import BulkOperationsToolbar from "./BulkOperationsToolbar";
import ResourceModal from "./ResourceModal";
import myAxios from "../../_services/axiositeachWrapper";
import Config from "../../_config/config";

type TableViewTabProps = {
  module: LearningModule | null;
  onUpdateSection: (chapterIndex: number, sectionIndex: number, updates: Partial<Step>) => void;
  onRemoveSection: (chapterIndex: number, sectionIndex: number) => void;
  onAddChapter: () => void;
  onAddSection: (chapterIndex: number) => void;
  onAddResource: (chapterIndex: number, sectionIndex: number) => void;
  onRemoveResource: (chapterIndex: number, sectionIndex: number, resourceIndex: number) => void;
  onUpdateResource: (chapterIndex: number, sectionIndex: number, resourceIndex: number, updates: Partial<Resource>) => void;
  availableTags: any[];
  resourceTypes: ResourceType[];
  isViewMode: boolean;
};

export default function TableViewTab({
  module,
  onUpdateSection,
  onRemoveSection,
  onAddChapter,
  onAddSection,
  onAddResource,
  onRemoveResource,
  onUpdateResource,
  availableTags,
  resourceTypes,
  isViewMode,
}: TableViewTabProps) {
  const [filterText, setFilterText] = useState("");
  const [collapsedChapters, setCollapsedChapters] = useState<Set<number>>(new Set());
  const [sessionTypes, setSessionTypes] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    myAxios.get(`${Config.nodeSiteUrl}v1/academic/classroom-session-type-mappings`)
      .then((res: any) => {
        if (res.data?.success && res.data.data?.sessionTypes) {
          setSessionTypes(res.data.data.sessionTypes.map((st: any) => ({ id: st.sessionTypeId, name: st.sessionTypeName })));
        }
      })
      .catch(() => {
        // Fallback: try lesson_plan_session_type_master directly if mapping endpoint not available
      });
  }, []);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<{
    chapterIndex: number;
    sectionIndex: number;
    chapterName: string;
    sectionName: string;
    resources: Resource[];
  } | null>(null);

  // Flatten hierarchy into table rows
  const flattenedSections = useMemo<FlattenedSection[]>(() => {
    if (!module) return [];

    const flattened: FlattenedSection[] = [];
    module.groups.forEach((chapter, chapterIndex) => {
      chapter.steps.forEach((section, sectionIndex) => {
        flattened.push({
          chapterIndex,
          chapterName: chapter.name,
          chapterColor: chapter.color,
          section,
          sectionIndex,
        });
      });
    });
    return flattened;
  }, [module]);

  // Filter sections
  const filteredSections = useMemo(() => {
    if (!filterText.trim()) return flattenedSections;
    const lower = filterText.toLowerCase();
    return flattenedSections.filter(
      (row) =>
        row.section.name.toLowerCase().includes(lower) ||
        row.section.code.toLowerCase().includes(lower) ||
        row.chapterName.toLowerCase().includes(lower)
    );
  }, [flattenedSections, filterText]);

  // Group by chapter
  const sectionsByChapter = useMemo(() => {
    const grouped = new Map<number, FlattenedSection[]>();
    filteredSections.forEach((row) => {
      if (!grouped.has(row.chapterIndex)) {
        grouped.set(row.chapterIndex, []);
      }
      grouped.get(row.chapterIndex)!.push(row);
    });
    return grouped;
  }, [filteredSections]);

  const toggleChapter = (chapterIndex: number) => {
    const newSet = new Set(collapsedChapters);
    if (newSet.has(chapterIndex)) {
      newSet.delete(chapterIndex);
    } else {
      newSet.add(chapterIndex);
    }
    setCollapsedChapters(newSet);
  };

  const handleExportCSV = () => {
    if (!module) return;

    const headers = ["Chapter", "Section", "Code", "Start Date", "End Date", "Resources", "Sort Order", "Week", "Session Type", "Splits", "Split Size"];
    const rows = flattenedSections.map((row) => [
      row.chapterName,
      row.section.name,
      row.section.code,
      row.section.startDate || "",
      row.section.endDate || "",
      row.section.resources.length.toString(),
      row.section.sortOrder.toString(),
      row.section.weekIndex != null ? row.section.weekIndex.toString() : "",
    ]);

    const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${module.code}_sections.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkSetDates = (startDate: string, endDate: string) => {
    if (!module) return;

    module.groups.forEach((chapter, ci) => {
      chapter.steps.forEach((_, si) => {
        onUpdateSection(ci, si, {
          startDate: startDate || null,
          endDate: endDate || null
        } as Partial<Step>);
      });
    });
  };

  const handleSpreadDates = (rangeStart: string, rangeEnd: string) => {
    if (!module) return;

    // Flatten all sections in chapter order
    const allSections: { ci: number; si: number }[] = [];
    module.groups.forEach((chapter, ci) => {
      chapter.steps.forEach((_, si) => {
        allSections.push({ ci, si });
      });
    });

    if (allSections.length === 0) return;

    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    const totalMs = end.getTime() - start.getTime();
    const totalDays = totalMs / (1000 * 60 * 60 * 24);

    if (totalDays < 0) return;

    const count = allSections.length;
    const msPerSection = totalMs / count;

    allSections.forEach(({ ci, si }, i) => {
      const sectionStart = new Date(start.getTime() + i * msPerSection);
      const sectionEnd = i === count - 1
        ? end
        : new Date(start.getTime() + (i + 1) * msPerSection - 1000 * 60 * 60 * 24);

      // Ensure end is not before start for very small ranges
      const finalEnd = sectionEnd < sectionStart ? sectionStart : sectionEnd;

      const fmt = (d: Date) => d.toISOString().split('T')[0];

      onUpdateSection(ci, si, {
        startDate: fmt(sectionStart),
        endDate: fmt(finalEnd),
      } as Partial<Step>);
    });
  };

  const totalSectionCount = module
    ? module.groups.reduce((sum, ch) => sum + ch.steps.length, 0)
    : 0;

  const handleViewResources = (row: FlattenedSection) => {
    setSelectedSection({
      chapterIndex: row.chapterIndex,
      sectionIndex: row.sectionIndex,
      chapterName: row.chapterName,
      sectionName: row.section.name,
      resources: row.section.resources,
    });
    setResourceModalOpen(true);
  };

  const handleAddResourceFromModal = () => {
    if (selectedSection) {
      onAddResource(selectedSection.chapterIndex, selectedSection.sectionIndex);
      setResourceModalOpen(false);
    }
  };

  const handleRemoveResourceFromModal = (resourceIndex: number) => {
    if (selectedSection) {
      onRemoveResource(selectedSection.chapterIndex, selectedSection.sectionIndex, resourceIndex);
      setResourceModalOpen(false);
    }
  };

  if (!module) {
    return (
      <div className="lmb-table-view-tab">
        <div className="lmb-empty">No module data to display.</div>
      </div>
    );
  }

  if (module.groups.length === 0) {
    return (
      <div className="lmb-table-view-tab">
        <div className="lmb-empty" style={{ padding: 40 }}>
          <p><strong>No chapters or sections yet.</strong></p>
          <p className="lmb-muted" style={{ marginTop: 8 }}>
            Switch to the Builder tab to add chapters and sections, or use the AI Assistant in the Overview tab to generate content.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lmb-table-view-tab">
      <div className="lmb-card">
        <div className="lmb-card-hd">
          <div><strong>📊 Table View</strong></div>
          <div className="lmb-stack">
            <span className="lmb-muted" style={{ fontSize: 13 }}>
              {flattenedSections.length} section{flattenedSections.length !== 1 ? "s" : ""}
            </span>
            {!isViewMode && (
              <button className="lmb-btn lmb-btn-sm" onClick={onAddChapter} type="button">
                + Add Chapter
              </button>
            )}
          </div>
        </div>

        <div className="lmb-card-bd">
          <BulkOperationsToolbar
            onExportCSV={handleExportCSV}
            onBulkSetDates={handleBulkSetDates}
            onSpreadDates={handleSpreadDates}
            filterText={filterText}
            onFilterChange={setFilterText}
            sectionCount={totalSectionCount}
          />

          <div className="lmb-table-container">
            <table className="lmb-table">
              <thead>
                <tr>
                  <th style={{ width: "30px" }}></th>
                  <th>Section</th>
                  <th>Code</th>
                  <th style={{ width: "140px" }}>Start Date</th>
                  <th style={{ width: "140px" }}>End Date</th>
                  <th style={{ width: "80px" }}>Resources</th>
                  <th style={{ width: "80px" }}>Sort Order</th>
                  <th style={{ width: "70px" }}>Week</th>
                  <th style={{ width: "120px" }}>Session Type</th>
                  <th style={{ width: "60px" }}>Splits</th>
                  <th style={{ width: "70px" }}>Split Size</th>
                  {!isViewMode && <th style={{ width: "80px" }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {Array.from(sectionsByChapter.entries()).map(([chapterIndex, sections]) => {
                  const isCollapsed = collapsedChapters.has(chapterIndex);
                  const chapter = module.groups[chapterIndex];

                  return (
                    <React.Fragment key={chapterIndex}>
                      {/* Chapter Header Row */}
                      <tr className="lmb-table-chapter-row">
                        <td>
                          <button
                            className="lmb-table-toggle"
                            onClick={() => toggleChapter(chapterIndex)}
                            type="button"
                          >
                            {isCollapsed ? "▶" : "▼"}
                          </button>
                        </td>
                        <td
                          colSpan={isViewMode ? 7 : 8}
                          style={{ borderLeft: `4px solid ${chapter.color}` }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                              <strong>
                                Chapter {chapterIndex + 1}: {chapter.name}
                              </strong>
                              <span className="lmb-muted" style={{ marginLeft: 8, fontSize: 12 }}>
                                ({sections.length} section{sections.length !== 1 ? "s" : ""})
                              </span>
                            </div>
                            {!isViewMode && (
                              <button
                                className="lmb-btn lmb-btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddSection(chapterIndex);
                                }}
                                type="button"
                              >
                                + Add Section
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Section Rows */}
                      {!isCollapsed &&
                        sections.map((row) => (
                          <tr key={`${row.chapterIndex}-${row.sectionIndex}`} className="lmb-table-section-row">
                            <td></td>
                            <td>
                              <input
                                className="lmb-table-input"
                                value={row.section.name}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, { name: e.target.value })
                                }
                                disabled={isViewMode}
                              />
                            </td>
                            <td>
                              <input
                                className="lmb-table-input"
                                value={row.section.code}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, { code: e.target.value })
                                }
                                disabled={isViewMode}
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                className="lmb-table-input"
                                value={row.section.startDate || ""}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, {
                                    startDate: e.target.value || null
                                  } as Partial<Step>)
                                }
                                disabled={isViewMode}
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                className="lmb-table-input"
                                value={row.section.endDate || ""}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, {
                                    endDate: e.target.value || null
                                  } as Partial<Step>)
                                }
                                disabled={isViewMode}
                              />
                            </td>
                            <td className="lmb-table-center">
                              <button
                                className="lmb-resource-count lmb-resource-count-btn"
                                onClick={() => handleViewResources(row)}
                                title={`Click to view/edit ${row.section.resources.length} resource(s)`}
                                type="button"
                              >
                                📎 {row.section.resources.length}
                              </button>
                            </td>
                            <td className="lmb-table-center">
                              <input
                                type="number"
                                className="lmb-table-input"
                                style={{ width: "60px", textAlign: "center" }}
                                value={row.section.sortOrder}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, {
                                    sortOrder: parseInt(e.target.value, 10) || 0,
                                  })
                                }
                                disabled={isViewMode}
                                min={0}
                              />
                            </td>
                            <td className="lmb-table-center">
                              <input
                                type="number"
                                className="lmb-table-input"
                                style={{ width: "55px", textAlign: "center" }}
                                value={row.section.weekIndex || ""}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, {
                                    weekIndex: e.target.value ? parseInt(e.target.value, 10) : null,
                                  } as Partial<Step>)
                                }
                                disabled={isViewMode}
                                min={1}
                                placeholder="—"
                                title="Week number (1-based)"
                              />
                            </td>
                            <td className="lmb-table-center">
                              <select
                                className="lmb-table-input"
                                style={{ width: "110px", padding: "2px 4px" }}
                                value={row.section.sessionTypeId || ""}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, {
                                    sessionTypeId: e.target.value ? parseInt(e.target.value, 10) : null,
                                  } as Partial<Step>)
                                }
                                disabled={isViewMode}
                              >
                                <option value="">—</option>
                                {sessionTypes.map((st) => (
                                  <option key={st.id} value={st.id}>{st.name}</option>
                                ))}
                              </select>
                            </td>
                            <td className="lmb-table-center">
                              <input
                                type="number"
                                className="lmb-table-input"
                                style={{ width: "50px", textAlign: "center" }}
                                value={row.section.numberOfSplits || 1}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, {
                                    numberOfSplits: parseInt(e.target.value, 10) || 1,
                                  } as Partial<Step>)
                                }
                                disabled={isViewMode}
                                min={1}
                                max={10}
                                title="Number of splits (1 = no split)"
                              />
                            </td>
                            <td className="lmb-table-center">
                              <input
                                type="number"
                                className="lmb-table-input"
                                style={{ width: "60px", textAlign: "center" }}
                                value={row.section.splitSize || ""}
                                onChange={(e) =>
                                  onUpdateSection(row.chapterIndex, row.sectionIndex, {
                                    splitSize: e.target.value ? parseInt(e.target.value, 10) : null,
                                  } as Partial<Step>)
                                }
                                disabled={isViewMode || (row.section.numberOfSplits || 1) <= 1}
                                placeholder="Auto"
                                title="Students per split (leave empty for auto-calculation)"
                              />
                            </td>
                            {!isViewMode && (
                              <td className="lmb-table-center">
                                <button
                                  className="lmb-table-action-btn"
                                  onClick={() => onRemoveSection(row.chapterIndex, row.sectionIndex)}
                                  title="Delete section"
                                  type="button"
                                >
                                  🗑️
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredSections.length === 0 && filterText && (
            <div className="lmb-empty" style={{ marginTop: 20 }}>
              No sections match your filter: "{filterText}"
            </div>
          )}
        </div>
      </div>

      {/* Resource Modal */}
      {selectedSection && (
        <ResourceModal
          isOpen={resourceModalOpen}
          onClose={() => setResourceModalOpen(false)}
          resources={selectedSection.resources}
          sectionName={selectedSection.sectionName}
          chapterName={selectedSection.chapterName}
          chapterIndex={selectedSection.chapterIndex}
          sectionIndex={selectedSection.sectionIndex}
          onAddResource={handleAddResourceFromModal}
          onRemoveResource={handleRemoveResourceFromModal}
          onUpdateResource={onUpdateResource}
          availableTags={availableTags}
          resourceTypes={resourceTypes}
          isViewMode={isViewMode}
        />
      )}
    </div>
  );
}
